import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useParams, useLocation } from 'wouter';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  limit as firestoreLimit,
  getDocs,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { useSendMessage } from '@/lib/api/useChat';
import { getFirestoreDb } from '@/lib/firebase';
import { useConversations } from './ConversationsContext';
import type { ConversationData, MessageData, PendingMessage } from './types';

const MESSAGES_PER_PAGE = 20;

interface SelectedConversationContextValue {
  selectedConversation: ConversationData | null;
  messages: MessageData[];
  pendingMessages: PendingMessage[];
  messageText: string;
  newestMessageId: string | null;
  setMessageText: (text: string) => void;
  selectConversation: (conversation: ConversationData | null) => void;
  sendMessage: () => Promise<void>;
  retryMessage: (message: PendingMessage) => Promise<void>;
  dismissFailedMessage: (messageId: string) => void;
  loadMoreMessages: () => Promise<void>;
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
}

const SelectedConversationContext = createContext<SelectedConversationContextValue | null>(null);

export function useSelectedConversation() {
  const context = useContext(SelectedConversationContext);
  if (!context) {
    throw new Error('useSelectedConversation must be used within a SelectedConversationProvider');
  }
  return context;
}

interface SelectedConversationProviderProps {
  children: ReactNode;
}

export function SelectedConversationProvider({ children }: SelectedConversationProviderProps) {
  const { conversations, userId } = useConversations();
  
  // URL routing
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
  const [, navigate] = useLocation();

  // State
  const [selectedConversation, setSelectedConversation] = useState<ConversationData | null>(null);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [messageText, setMessageText] = useState('');
  const [newestMessageId, setNewestMessageId] = useState<string | null>(null);
  
  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const oldestDocRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

  // Send message mutation
  const sendMessageMutation = useSendMessage();

  // Sync selected conversation with URL
  useEffect(() => {
    if (urlConversationId && conversations.length > 0) {
      const conversation = conversations.find((c) => c.id === urlConversationId);
      if (conversation && selectedConversation?.id !== urlConversationId) {
        setSelectedConversation(conversation);
      }
    } else if (!urlConversationId && selectedConversation) {
      setSelectedConversation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConversationId, conversations]);

  // Select conversation and update URL
  const selectConversation = useCallback(
    (conversation: ConversationData | null) => {
      if (selectedConversation?.id === conversation?.id) {
        return;
      }
      setSelectedConversation(conversation);
      // Reset pagination state when switching conversations
      setMessages([]);
      setHasMoreMessages(true);
      oldestDocRef.current = null;
      if (conversation) {
        navigate(`/chat/${conversation.id}`);
      } else {
        navigate('/chat');
      }
    },
    [navigate, selectedConversation?.id]
  );

  // Subscribe to messages when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;

    // Reset state for new conversation
    setHasMoreMessages(true);
    setNewestMessageId(null);
    oldestDocRef.current = null;

    const db = getFirestoreDb();
    const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
    // Listen to the most recent messages in real-time
    const q = query(messagesRef, orderBy('createdAt', 'desc'), firestoreLimit(MESSAGES_PER_PAGE));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const recentMsgs: MessageData[] = [];
        let oldestDoc: QueryDocumentSnapshot<DocumentData> | null = null;
        
        snapshot.forEach((docSnap) => {
          recentMsgs.unshift({ id: docSnap.id, ...docSnap.data() } as MessageData);
          oldestDoc = docSnap; // Last doc in desc order is the oldest
        });

        // Track the newest message ID for scroll detection
        const newestMsg = recentMsgs[recentMsgs.length - 1];
        if (newestMsg) {
          setNewestMessageId(newestMsg.id);
        }
        
        // Merge with any older messages we've loaded via pagination
        setMessages((prevMessages) => {
          if (prevMessages.length === 0) {
            return recentMsgs;
          }

          // Get IDs of messages from the real-time query
          const recentIds = new Set(recentMsgs.map(m => m.id));
          
          // Keep older messages that aren't in the recent query
          const olderMessages = prevMessages.filter(m => !recentIds.has(m.id));
          
          // Combine: older messages first, then recent messages
          // Sort by createdAt to ensure proper order
          const combined = [...olderMessages, ...recentMsgs];
          combined.sort((a, b) => {
            const timeA = a.createdAt?.toMillis?.() || 0;
            const timeB = b.createdAt?.toMillis?.() || 0;
            return timeA - timeB;
          });
          
          return combined;
        });

        // Store reference to oldest doc for pagination
        if (oldestDoc && !oldestDocRef.current) {
          oldestDocRef.current = oldestDoc;
        }

        // Check if we might have more messages
        setHasMoreMessages(snapshot.size >= MESSAGES_PER_PAGE);

        // Mark conversation as read
        if (userId && selectedConversation.unreadCounts?.[userId] > 0) {
          const conversationRef = doc(db, 'conversations', selectedConversation.id);
          updateDoc(conversationRef, {
            [`unreadCounts.${userId}`]: 0,
          }).catch(console.error);
        }
      },
      (error) => {
        console.error('Error fetching messages:', error);
      }
    );

    return unsubscribe;
  }, [selectedConversation, userId]);

  // Load more (older) messages
  const loadMoreMessages = useCallback(async () => {
    if (!selectedConversation || !hasMoreMessages || isLoadingMore) {
      return;
    }

    // Need the oldest message to paginate from
    if (messages.length === 0) {
      return;
    }

    setIsLoadingMore(true);

    try {
      const db = getFirestoreDb();
      const messagesRef = collection(db, 'conversations', selectedConversation.id, 'messages');
      
      // Get the oldest message's timestamp for cursor
      const oldestMessage = messages[0];
      const oldestTimestamp = oldestMessage.createdAt;

      // Query for older messages
      const q = query(
        messagesRef,
        orderBy('createdAt', 'desc'),
        startAfter(oldestTimestamp),
        firestoreLimit(MESSAGES_PER_PAGE)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setHasMoreMessages(false);
        setIsLoadingMore(false);
        return;
      }

      const olderMessages: MessageData[] = [];
      snapshot.forEach((docSnap) => {
        olderMessages.unshift({ id: docSnap.id, ...docSnap.data() } as MessageData);
      });

      // Prepend older messages
      setMessages((prev) => [...olderMessages, ...prev]);
      
      // Check if there might be more
      setHasMoreMessages(snapshot.size >= MESSAGES_PER_PAGE);
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [selectedConversation, hasMoreMessages, isLoadingMore, messages]);

  // Clear pending messages when real messages arrive with matching content
  useEffect(() => {
    if (messages.length > 0 && pendingMessages.length > 0) {
      const confirmedContent = new Set(
        messages.filter((m) => m.senderId === userId).map((m) => m.content)
      );
      setPendingMessages((prev) =>
        prev.filter((pm) => pm.status === 'failed' || !confirmedContent.has(pm.content))
      );
    }
  }, [messages, userId, pendingMessages.length]);

  // Send message via server API with optimistic update
  const sendMessage = useCallback(async () => {
    if (!messageText.trim() || !selectedConversation || !userId) return;

    const text = messageText.trim();
    const tempId = `pending_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    const pendingMessage: PendingMessage = {
      id: tempId,
      senderId: userId,
      content: text,
      type: 'text',
      createdAt: new Date(),
      status: 'sending',
    };

    setPendingMessages((prev) => [...prev, pendingMessage]);
    setMessageText('');

    try {
      const result = await sendMessageMutation.mutateAsync({
        conversationId: selectedConversation.id,
        content: text,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setPendingMessages((prev) =>
        prev.map((pm) => (pm.id === tempId ? { ...pm, status: 'failed' as const } : pm))
      );
    }
  }, [messageText, selectedConversation, userId, sendMessageMutation]);

  // Retry a failed message
  const retryMessage = useCallback(
    async (pendingMessage: PendingMessage) => {
      if (!selectedConversation || pendingMessage.status !== 'failed') return;

      setPendingMessages((prev) =>
        prev.map((pm) => (pm.id === pendingMessage.id ? { ...pm, status: 'sending' as const } : pm))
      );

      try {
        const result = await sendMessageMutation.mutateAsync({
          conversationId: selectedConversation.id,
          content: pendingMessage.content,
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to send message');
        }
      } catch (error) {
        console.error('Error retrying message:', error);
        setPendingMessages((prev) =>
          prev.map((pm) =>
            pm.id === pendingMessage.id ? { ...pm, status: 'failed' as const } : pm
          )
        );
      }
    },
    [selectedConversation, sendMessageMutation]
  );

  // Remove a failed message
  const dismissFailedMessage = useCallback((messageId: string) => {
    setPendingMessages((prev) => prev.filter((pm) => pm.id !== messageId));
  }, []);

  const value: SelectedConversationContextValue = {
    selectedConversation,
    messages,
    pendingMessages,
    messageText,
    newestMessageId,
    setMessageText,
    selectConversation,
    sendMessage,
    retryMessage,
    dismissFailedMessage,
    loadMoreMessages,
    hasMoreMessages,
    isLoadingMore,
  };

  return (
    <SelectedConversationContext.Provider value={value}>
      {children}
    </SelectedConversationContext.Provider>
  );
}
